/**
 * Provider Management Hooks
 * Phase 4: Training Provider Expansion Framework
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { 
  TrainingProviderExtended, 
  TrainingProviderSource, 
  ProviderAuditLogEntry,
  ProviderManageAction 
} from '@/types/training';

/**
 * Fetch all providers with extended Phase 4 fields
 */
export function useProvidersExtended() {
  return useQuery({
    queryKey: ['training-providers-extended'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('training_providers')
        .select('*')
        .order('display_name');

      if (error) throw error;
      return data as TrainingProviderExtended[];
    },
  });
}

/**
 * Fetch sources for a specific provider
 */
export function useProviderSources(providerId: string | null) {
  return useQuery({
    queryKey: ['provider-sources', providerId],
    queryFn: async () => {
      if (!providerId) return [];
      
      const { data, error } = await supabase
        .from('training_provider_sources')
        .select('*')
        .eq('provider_id', providerId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as TrainingProviderSource[];
    },
    enabled: !!providerId,
  });
}

/**
 * Fetch audit log for a provider
 */
export function useProviderAuditLog(providerId: string | null, limit = 50) {
  return useQuery({
    queryKey: ['provider-audit-log', providerId, limit],
    queryFn: async () => {
      if (!providerId) return [];
      
      const { data, error } = await supabase
        .from('provider_audit_log')
        .select('*')
        .eq('provider_id', providerId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data as ProviderAuditLogEntry[];
    },
    enabled: !!providerId,
  });
}

interface ManageProviderInput {
  action: ProviderManageAction;
  providerId: string;
  userId?: string;
  reason?: string;
  data?: Record<string, unknown>;
}

/**
 * Mutation hook for provider management actions
 */
export function useManageProvider() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: ManageProviderInput) => {
      const { data, error } = await supabase.functions.invoke('manage-provider', {
        body: input,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['training-providers'] });
      queryClient.invalidateQueries({ queryKey: ['training-providers-extended'] });
      queryClient.invalidateQueries({ queryKey: ['provider-sources', variables.providerId] });
      queryClient.invalidateQueries({ queryKey: ['provider-audit-log', variables.providerId] });
    },
  });
}

interface CreateProviderInput {
  display_name: string;
  provider_type?: string;
  website_url?: string;
  description?: string;
  logo_url?: string;
  discovery_enabled?: boolean;
  learning_enabled?: boolean;
}

/**
 * Create a new provider
 */
export function useCreateProvider() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateProviderInput) => {
      const { data, error } = await supabase.functions.invoke('manage-provider', {
        body: {
          action: 'create',
          // No providerId for create action
          data: input,
        },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['training-providers'] });
      queryClient.invalidateQueries({ queryKey: ['training-providers-extended'] });
    },
  });
}

/**
 * Dry-run discovery for a provider
 */
export function useDryRunDiscovery() {
  return useMutation({
    mutationFn: async (providerId: string) => {
      const { data, error } = await supabase.functions.invoke('fetch-training-sources', {
        body: {
          provider_id: providerId,
          trigger: 'manual',
          dry_run: true,
        },
      });

      if (error) throw error;
      return data;
    },
  });
}

/**
 * Run discovery now for a provider (actual run, not dry-run)
 */
export function useRunProviderDiscovery() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (providerId: string) => {
      const { data, error } = await supabase.functions.invoke('fetch-training-sources', {
        body: {
          provider_id: providerId,
          trigger: 'manual',
        },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['training-sources'] });
      queryClient.invalidateQueries({ queryKey: ['pending-discoveries'] });
      queryClient.invalidateQueries({ queryKey: ['discovery-summary'] });
    },
  });
}
